// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, inject, signal, computed, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { datadogRum } from '@datadog/browser-rum';
import { KeyProjectDetailHeaderComponent } from '@lfx/key-projects/components/key-project-detail-header/key-project-detail-header.component';
import { HealthScoreVariant, ProjectDetailResponse } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsService } from '@lfx/key-projects/services/key-projects.service';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { ShortNumberPipe } from '@lfx/shared/pipes/short-number.pipe';
import { TimeRange, TimeRangeValue } from '@lfx/key-projects/entities';

@Component({
  selector: 'lfx-key-project-detail',
  imports: [CommonModule, KeyProjectDetailHeaderComponent, RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
      template: `
    <div class="key-project-detail-page">
      <!-- Loading State -->
      <div *ngIf="loading()" class="loading-container">
        <i class="fal fa-spinner-third fa-spin"></i>
        <span>Loading project details...</span>
      </div>

      <!-- Error State -->
      <div *ngIf="error()" class="error-container">
        <i class="fal fa-exclamation-triangle"></i>
        <span>{{ error() }}</span>
      </div>

      <!-- Project Header -->
      <lfx-key-project-detail-header
        *ngIf="projectData() && !loading() && !error()"
        [slug]="currentSlug()"
        [projectName]="projectData()?.name"
        [logoUrl]="projectData()?.logo"
        [description]="projectData()?.description"
        [healthScoreVariant]="healthScoreVariant()"
        [firstCommit]="formattedFirstCommit()"
        [softwareValue]="formattedSoftwareValue()"
        [activeTab]="currentTab()"
        [selectedRange]="currentTimeRange()"
        (tabChanged)="onTabChanged($event)"
        (rangeChanged)="onTimeRangeChanged($event)">
      </lfx-key-project-detail-header>

      <!-- Child Route Content -->
      <div *ngIf="projectData() && !loading() && !error()">
        <router-outlet></router-outlet>
      </div>
    </div>
  `,
  styles: [`
    .loading-container,
    .error-container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--lf-spacing-12);
      padding: var(--lf-spacing-48);
      text-align: center;
    }

    .loading-container i {
      color: var(--lf-color-primary);
      font-size: var(--lf-font-size-lg);
    }

    .error-container {
      color: var(--lf-color-danger);
    }

    .error-container i {
      font-size: var(--lf-font-size-lg);
    }
  `]
})
export class KeyProjectDetailComponent implements OnDestroy {
  private readonly keyProjectsService = inject(KeyProjectsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly shortNumberPipe = inject(ShortNumberPipe);

  // State signals
  private readonly _projectData = signal<ProjectDetailResponse | null>(null);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _currentSlug = signal<string>('');
  private readonly _currentTab = signal<string>('our-influence');
  private readonly _currentTimeRange = signal<TimeRangeValue>(TimeRange.DEFAULT);

  // Read-only accessors
  readonly projectData = this._projectData.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly currentSlug = this._currentSlug.asReadonly();
  readonly currentTab = this._currentTab.asReadonly();
  readonly currentTimeRange = this._currentTimeRange.asReadonly();

  // Computed properties for formatted data
  readonly healthScoreVariant = computed((): HealthScoreVariant | undefined => {
    const data = this.projectData();
    if (!data?.healthScore) return undefined;

    // Convert numeric health score to variant using existing helper method
    const healthScoreObject = KeyProjectsHelper.mapHealthScoreToLevel(data.healthScore);
    return healthScoreObject.level;
  });

  readonly formattedFirstCommit = computed(() => {
    const data = this.projectData();
    if (!data?.firstCommit) return '';

    // Format the date string (e.g., "2001-03-30 14:47:27.000" -> "March 2001")
    const date = new Date(data.firstCommit);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  });

  readonly formattedSoftwareValue = computed(() => {
    const data = this.projectData();
    if (!data?.softwareValue) return '';

    const shortNumber = this.shortNumberPipe.transform(data.softwareValue);
    return `$${shortNumber}`;
  });

  constructor() {
    // Subscribe to route params to get the slug
    this.activatedRoute.params.subscribe((params) => {
      const slug = params['slug'];
      if (slug) {
        this._currentSlug.set(slug);
        this.loadProjectData(slug);
      }
    });

    // Subscribe to query params to get the time range
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed())
      .subscribe((queryParams) => {
        // Handle time range
        const timeRange = queryParams['timeRange'];
        if (TimeRange.isValid(timeRange)) {
          this._currentTimeRange.set(timeRange);
        } else if (!queryParams['timeRange']) {
          this.updateTimeRangeInUrl(TimeRange.DEFAULT);
        }
      });

    // Subscribe to child route changes to update current tab
    this.router.events
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const currentRoute = this.activatedRoute.firstChild;
          if (currentRoute) {
            const snapshot = currentRoute.snapshot;
            const routePath = snapshot.routeConfig?.path;
            if (routePath && ['our-influence', 'leaderboards'].includes(routePath)) {
              this._currentTab.set(routePath);
            }
          }
        }
      });
  }

  private loadProjectData(slug: string): void {
    this._loading.set(true);
    this._error.set(null);

    this.keyProjectsService.getProjectDetails(slug).subscribe({
      next: (data) => {
        this._projectData.set(data);
        this._loading.set(false);
        this.setDatadogProjectContext(slug, data.name);
      },
      error: (err) => {
        console.error('Failed to load project details:', err);
        this._error.set('Failed to load project details. Please try again.');
        this._loading.set(false);
      }
    });
  }

  onTabChanged(tab: string): void {
    this.router.navigate([tab], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve'
    });
  }

  onTimeRangeChanged(timeRange: TimeRangeValue): void {
    this._currentTimeRange.set(timeRange);
    this.updateTimeRangeInUrl(timeRange);
  }

  private updateTimeRangeInUrl(timeRange: TimeRangeValue): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { timeRange },
      queryParamsHandling: 'merge'
    });
  }

  ngOnDestroy(): void {
    this.clearDatadogProjectContext();
  }

  private setDatadogProjectContext(slug: string, name: string): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.setGlobalContextProperty('keyProject', {
        slug,
        name,
        page: 'detail'
      });
    }
  }

  private clearDatadogProjectContext(): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.removeGlobalContextProperty('keyProject');
    }
  }
}
